// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { CommissionTypes, CommissionOperationType } from '@shared/utils/Commission/CommissionEnums';
import MarginOEControlTemplate from '@front/templates/MarginOEControlTemplate.html';
import { VerticalApplicationPanelNew } from '../cache/VerticalPanel/VerticalApplicationPanelNew';
import { VerticalPanelDataProvider } from '../cache/VerticalPanel/VerticalPanelDataProvider';
import { VerticalPanelDataProviderItem } from '../cache/VerticalPanel/VerticalPanelDataProviderItem';
import { Control } from '../elements/Control';
import { QuickTableRow } from '../elements/QuickTable/QuickTableRow';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { QuotingType } from '@shared/utils/Instruments/QuotingType';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { DynProperty } from '@shared/commons/DynProperty';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OrderEditBaseUtils } from '@shared/utils/Trading/OrderEditBaseUtils';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { MarginInfoParameters } from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoParameters';
import { DirectMarginMessage } from '@shared/utils/DirectMessages/DirectMarginMessage';
import { InstrumentFeatureHelper } from '@shared/utils/Instruments/InstrumentFeatureHelper';
import { AssetBalanceItemColumnsEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { StringUtils } from '@shared/utils/StringUtils';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import throttle from 'lodash.throttle';
import * as MARGIN_INFO_KEYS from '@shared/commons/UtilsClasses/MarginInfo/MarginInfoKeys';
export class MarginOEControl extends VerticalApplicationPanelNew {
    constructor() {
        super();
        this.settings = null;
        this.lastReceivedResponseMessage = null;
        this.allowedCommissionTypes = null;
        this.marginAbortController = null;
        this.refreshTick = 0; // current tick
        this.refreshCycleFullTime = 30000; // full time in ms
        this.refreshTickTime = 1000; // timer timeout duration in ms
        this.refreshIntervalHandler = null; // setInterval handler
        this.lastLimitPrice = null; // кэшируем во избежание избыточного запроса при изменении orderEditParameterArray
        this.lastStopPrice = null; // ^_^ -!-
        this.lastAccount = null;
        this.lastInstrument = null;
        this.msgTimeoutHandler = null; // задержка перед отправкой запроса на сервер
        this.msgTimeoutDuration = 200; // длительность задержки в мс
        this.throttledParamsDuration = 2000;
        this.throttledRefreshByParams = throttle(() => {
            this.RefreshByMarginRequest();
        }, this.throttledParamsDuration, { leading: true, trailing: true });
        this.settings = SessionSettings;
    }
    getType() {
        return PanelNames.MarginOEControl;
    }
    oninit() {
        super.oninit();
        Control.Ticker.Subscribe(this.UpdateData, this);
        this.on('refreshBtnClicked', this.RefreshByMarginRequest);
        this.observe('account instrument', this.onUpdateMarginInfo);
        this.observe('operation orderType', this.RefreshByMarginRequest, { init: false });
        this.observe('amount cash leverageValue', this.throttledRefreshByParams, { init: false });
        this.observe('orderEditParameterArray', this.onOrderEditParameterArrayChange, { init: false });
        this.observe('visible', this.onVisibleChange, { init: false });
    }
    oncomplete() {
        super.oncomplete();
        this.InitTable();
    }
    localize() {
        super.localize();
        if (!isNullOrUndefined(this.VerticalPanelDataProvider)) {
            this.VerticalPanelDataProvider.Localize();
        }
        void this.set('refreshBtnTooltip', Resources.getResource('OrderEntry.InfoBlock.refreshBtnTooltip'));
    }
    InitializeDataProvider() {
        this.VerticalPanelDataProvider = new VerticalPanelDataProvider(this.settings);
        // Data items
        this.VerticalPanelDataProvider.items = this.getDataItems();
        this.VerticalPanelDataProvider.GetVerticalPanelValueHandler = this.GetDataValue.bind(this);
        this.VerticalPanelDataProvider.GetItemsIDHandler = this.GetItemsID.bind(this);
        this.VerticalPanelDataProvider.GetDynamicItemsHandler = this.GetDynamicItems.bind(this);
        this.VerticalPanelDataProvider.DisposeHandler = this.DisposeSource.bind(this);
        const account = this.get('account');
        if (!isNullOrUndefined(account)) {
            this.SetSource(DataCache, account.BstrAccount);
        }
    }
    RefreshByMarginRequest() {
        if (!this.get('visible')) {
            return;
        }
        this.invalidateMarginResponseIfReceived();
        this.invalidateEstQty();
        this.UpdateTable();
        if (!isNullOrUndefined(this.msgTimeoutHandler)) {
            clearTimeout(this.msgTimeoutHandler);
        }
        this.msgTimeoutHandler = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
            this.refreshTick = 0;
            clearTimeout(this.msgTimeoutHandler);
            const ins = this.get('instrument');
            const acc = this.get('account');
            const orderType = this.get('orderType');
            if (isNullOrUndefined(ins) || isNullOrUndefined(acc) || isNullOrUndefined(orderType)) {
                return;
            }
            const buyMarginInfoParameters = this.createMarginInfoParameters(OperationType.Buy);
            const sellMarginInfoParameters = this.createMarginInfoParameters(OperationType.Sell);
            const signal = this.resetMarginAbortController();
            yield this.sendMarginRequest(buyMarginInfoParameters, sellMarginInfoParameters, signal);
        }), this.msgTimeoutDuration);
    }
    sendMarginRequest(buyMarginInfoParameters, sellMarginInfoParameters, signal) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield DataCache.SendMarginRequest([buyMarginInfoParameters, sellMarginInfoParameters]);
            if (signal === null || signal === void 0 ? void 0 : signal.aborted) {
                return;
            }
            this.handleMarginResponse(resp);
        });
    }
    handleMarginResponse(resp) {
        let nextLastMessage;
        if (resp instanceof DirectMarginMessage) {
            const estQtyValue = this.IsBuy()
                ? resp.AmountBuy
                : resp.AmountSell;
            void this.set({ estQtyValue });
            nextLastMessage = resp;
        }
        else {
            nextLastMessage = undefined;
        }
        if (this.lastReceivedResponseMessage === null && nextLastMessage === undefined) {
            this.lastReceivedResponseMessage = null;
        }
        else {
            this.lastReceivedResponseMessage = nextLastMessage;
        }
        this.UpdateTable();
    }
    resetMarginAbortController() {
        if (this.marginAbortController) {
            this.marginAbortController.abort();
        }
        this.marginAbortController = new AbortController();
        return this.marginAbortController.signal;
    }
    invalidateEstQty() {
        void this.set({ estQtyValue: undefined });
    }
    invalidateMarginResponseIfReceived() {
        if (this.marginAbortController) {
            this.marginAbortController.abort();
            this.marginAbortController = null;
        }
        const hasReceivedMarginResponse = this.lastReceivedResponseMessage !== null;
        if (hasReceivedMarginResponse) {
            this.lastReceivedResponseMessage = undefined;
        }
    }
    createMarginInfoParameters(operation) {
        const ins = this.get('instrument');
        const acc = this.get('account');
        const orderType = this.get('orderType');
        const { isCashQtyMode, cash, productType, leverageValue, tif } = this.get();
        const quantity = this.get('amount');
        const amount = !isNullOrUndefined(quantity) ? Quantity.toLots(quantity, ins) : null;
        const price = this.GetLimitPrice();
        const stopPrice = this.GetStopPrice();
        const orderTypeID = orderType.id();
        const parameters = new MarginInfoParameters();
        parameters.account = acc;
        parameters.instrument = ins;
        parameters.operation = operation;
        parameters.orderType = orderTypeID;
        parameters.validity = tif;
        parameters.isCashMode = isCashQtyMode;
        parameters.cash = cash;
        parameters.amountLots = amount;
        parameters.price = price;
        parameters.stopPrice = stopPrice;
        parameters.productType = productType;
        parameters.leverageValue = leverageValue;
        return parameters;
    }
    onOrderEditParameterArrayChange() {
        const newLimitPrice = this.GetLimitPrice();
        const newStopPrice = this.GetStopPrice();
        if (this.lastLimitPrice !== newLimitPrice || this.lastStopPrice !== newStopPrice) {
            this.throttledRefreshByParams();
        }
        this.lastLimitPrice = newLimitPrice;
        this.lastStopPrice = newStopPrice;
    }
    InitTable() {
        const qtR = this.quickTableRactive;
        if (isNullOrUndefined(qtR))
            return;
        qtR.setShowColumnHeaders(false);
        const qt = qtR.quickTable;
        if (isNullOrUndefined(qt))
            return;
        qt.allowGroupBy = false;
        qt.useVerticalSeparatorForResizing = true;
        qt.FormatGroup = this.quickTable_FormatGroup.bind(this);
        // qt.OnPaintedPictureButtonClick.Subscribe(this.onPaintedPictureButtonClick, this);
    }
    UpdateTable() {
        this.PopulateTable();
        this.UpdateData();
    }
    UpdateData() {
        const qtRactive = this.quickTableRactive;
        const qt = !isNullOrUndefined(qtRactive) ? qtRactive.quickTable : null;
        let needRedraw = !isNullOrUndefined(qt) ? qt.needRedraw : null;
        if (!isNullOrUndefined(this.VerticalPanelDataProvider)) {
            this.VerticalPanelDataProvider.UpdateItems();
            // this.hideGroupsIfEmpty()
            needRedraw = true;
        }
        if (needRedraw != null) {
            qt.needRedraw = false;
            qt.Draw();
        }
    }
    layoutTable() {
        super.layoutTable();
        const qt = this.getQuickTable();
        if (isNullOrUndefined(qt))
            return;
        if (!isNullOrUndefined(qt.columns[0]) && !isNullOrUndefined(qt.columns[1])) {
            // установка ширин колонок - todo подумать как сделать это лучше
            qt.columns[0].width = 150;
            qt.columns[1].width = 150;
        }
    }
    quickTable_FormatGroup(groupValue) {
        const acc = this.get('account');
        const currency = !isNullOrUndefined(acc) ? ' ' + acc.BaseCurrency : '';
        return Resources.getResource(groupValue) + currency;
    }
    dispose() {
        if (this.marginAbortController) {
            this.marginAbortController.abort();
            this.marginAbortController = null;
        }
        this.lastReceivedResponseMessage = null;
        if (!isNullOrUndefined(this.refreshIntervalHandler)) {
            clearInterval(this.refreshIntervalHandler);
        }
        // if (this.getQuickTable())
        //     this.getQuickTable().OnPaintedPictureButtonClick.UnSubscribe(this.onPaintedPictureButtonClick, this);
        Control.Ticker.UnSubscribe(this.UpdateData, this);
        super.dispose();
    }
    resetMarginData() {
        this.lastReceivedResponseMessage = null;
        void this.set({ estQtyValue: null });
    }
    onUpdateMarginInfo() {
        this.resetMarginData();
        const account = this.get('account');
        const instrument = this.get('instrument');
        const noChanges = this.lastAccount === account && this.lastInstrument === instrument;
        if (isNullOrUndefined(account) || isNullOrUndefined(instrument) || noChanges) {
            return;
        }
        this.lastAccount = account;
        this.lastInstrument = instrument;
        this.allowedCommissionTypes = account.GetAllowedCommissionTypes(instrument);
        this.repopulate();
        this.RefreshByMarginRequest();
    }
    onVisibleChange(visible) {
        if (visible) {
            this.RefreshByMarginRequest();
            this.InitRefreshBtnTimer();
        }
        else if (!isNullOrUndefined(this.refreshIntervalHandler)) {
            clearInterval(this.refreshIntervalHandler);
        }
    }
    getDataItems() {
        const items = [];
        const keys = this.GetItemsID();
        for (let i = 0; i < keys.length; i++) {
            const k = keys[i];
            if (Resources.isHidden(k)) {
                continue;
            }
            const group = MarginOEControl.GetGroupForKey(k);
            if (group === MARGIN_INFO_KEYS.KEY_GROUP_FEES) {
                // вот так пока не добавляю поле из fees если их нет
                if (!isNullOrUndefined(this.allowedCommissionTypes)) {
                    const cType = MarginOEControl.KeyToCommissionType(k);
                    if (!this.allowedCommissionTypes[cType] && !isNullOrUndefined(cType)) {
                        continue;
                    }
                }
            }
            if (isValidString(group)) {
                const locKey = k;
                const newItem = new VerticalPanelDataProviderItem();
                newItem.Id = locKey;
                newItem.DynPropertyControltype = this.GetDynPropertyControlTypeForKey(k);
                newItem.SortIndex = MarginOEControl.GetSortIndex(k);
                newItem.LocalizationKey = locKey;
                // newItem.tooltipKey = Resources.GetToolTipKey(columnParams.HeaderKey);
                newItem.Group = group;
                items.push(newItem);
            }
        }
        return items;
    }
    GetDataValue(key, VerticalPanelDataProvider, _sett, rowId) {
        try {
            const qt = this.getQuickTable();
            const row = qt.rows[rowId];
            const accItems = VerticalPanelDataProvider.Source;
            let useAssetBalanceItem = true;
            const id = MarginOEControl.KEYS_MAP[key];
            if (!isValidNumber(id)) {
                useAssetBalanceItem = false;
            }
            if (isValidArray(accItems)) {
                const assetBalanceItem = accItems[0];
                let formattedValue = '';
                const isColoredField = MarginOEControl.isColoredField(key, assetBalanceItem);
                if (useAssetBalanceItem) {
                    formattedValue = assetBalanceItem.getColumnData(id).FormattedValue;
                }
                else {
                    formattedValue = this.getFormattedValueByKey(key, assetBalanceItem);
                }
                if (!isNullOrUndefined(row)) {
                    this.updateRowVisibilityAndColor(row, formattedValue, isColoredField);
                }
                return formattedValue;
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        return '';
    }
    getFormattedValueByKey(key, assetBalanceItem) {
        const side = this.getSide();
        const group = this.GetGroupBySide(side);
        const isMarginDep = MARGIN_INFO_KEYS.isMarginResponseDepKey(key);
        if (isMarginDep) {
            const isHiddenUntilResponse = MARGIN_INFO_KEYS.isHiddenUntilResponseKey(key);
            const isRecalculating = this.lastReceivedResponseMessage === undefined;
            const hasNoResponseYet = this.lastReceivedResponseMessage === null;
            const isCalculating = isRecalculating || (hasNoResponseYet && !isHiddenUntilResponse);
            if (isCalculating) {
                return Resources.getResource('general.Calculating');
            }
        }
        return this.getFormattedValue(key, group, assetBalanceItem);
    }
    getFormattedValue(key, group, assetBalanceItem) {
        switch (key) {
            case MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS:
                return this.formatPrice(group === null || group === void 0 ? void 0 : group.AvailableFunds);
            case MARGIN_INFO_KEYS.KEY_IMPACT_ON_FUNDS:
                return this.formatPrice(group === null || group === void 0 ? void 0 : group.ImpactOnFunds);
            case MARGIN_INFO_KEYS.KEY_IMPACT_ON_MARGIN:
                return this.GetImpactOnMarginFormatted(assetBalanceItem, group === null || group === void 0 ? void 0 : group.AfterTradeMargin);
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS:
                return this.formatPrice(group === null || group === void 0 ? void 0 : group.AfterTradeFunds);
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_MARGIN:
                return this.formatPrice(group === null || group === void 0 ? void 0 : group.AfterTradeMargin);
            case MARGIN_INFO_KEYS.KEY_SPREAD_INIT_LOSS:
                return this.GetSpreadInitLossFormatted();
            case MARGIN_INFO_KEYS.KEY_PL_PER_TICK:
                return this.GetPLPerTickFormatted();
            case MARGIN_INFO_KEYS.KEY_ALLOW_SHORT_POSITIONS:
                return InstrumentFeatureHelper.GetLocalizedAllowShortPosition(this.get('instrument').IsAllowShortPositions(this.get('productType')));
            case MARGIN_INFO_KEYS.KEY_EST_AVAILABLE_CASH:
                return this.GetEstAvailableCashFormatted();
            case MARGIN_INFO_KEYS.KEY_FILL_PER_LOT:
                return this.GetFillPerLotFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT:
                return this.GetOrderPerLotFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
                return this.GetOrderPerLotWithFixedPartFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_PER_FILL:
                return this.GetPerFillFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_PER_TRANSACTION:
                return this.GetPerTransactionFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_PER_PHONE_TRANSACTION:
                return this.GetPerPhoneTransactionFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_VAT:
                return this.GetVatFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME:
                return this.GetFillVolumeFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_ORDER_VOLUME:
                return this.GetOrderVolumeFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME_WITH_MIN_PD:
                return this.GetFillVolumeWithMinPDFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_SHORT_SWAP_INFO:
                return this.GetShortSwapInfo();
            case MARGIN_INFO_KEYS.KEY_FREE_COMMISSION:
                return this.GetFreeCommissionFormatted();
            case MARGIN_INFO_KEYS.KEY_TOTAL_FEES:
                return this.IsTotalFeeRowVisible() ? this.GetTotalFeeFormatted(group) : '';
            case MARGIN_INFO_KEYS.KEY_LONG_SWAP:
                return this.GetSwapFormatted(OperationType.Buy);
            case MARGIN_INFO_KEYS.KEY_SHORT_SWAP:
                return this.GetSwapFormatted(OperationType.Sell);
            case MARGIN_INFO_KEYS.KEY_ORDER_CARE:
                return this.GetCareOrderFeeFormatted();
            case MARGIN_INFO_KEYS.KEY_FILL_FACE_VALUE:
                return this.GetFillFaceValueFeeFormatted();
            default: {
                const addInfo = assetBalanceItem.AssetBalance.AssetAdditionalInfo;
                if (!isNullOrUndefined(addInfo === null || addInfo === void 0 ? void 0 : addInfo[key])) {
                    return assetBalanceItem.AssetBalance.formatPriceExactly(addInfo[key].Value); // custom available funds #106952
                }
                return '';
            }
        }
    }
    updateRowVisibilityAndColor(row, formattedValue, isColoredField) {
        if (!isValidString(formattedValue)) {
            row.visible = false;
        }
        const val = parseFloat(formattedValue.replace(',', '.'));
        if (isColoredField && isValidNumber(val)) {
            // красим пока только это поле
            row.cells[1].ForeColor = QuickTableRow.ColorBySign(row.table.columns[1].EmptyValue, val);
        }
    }
    IsNoneMarginType() {
        var _a;
        const ins = this.get('instrument');
        return (_a = ins === null || ins === void 0 ? void 0 : ins.IsNoneMarginType(this.getAccount(), this.get('productType'))) !== null && _a !== void 0 ? _a : false;
    }
    GetItemsID() {
        let result = MarginOEControl.ALL_FIELDS;
        if (this.IsNoneMarginType()) {
            /* #99902 */
            result = result.filter((item) => item !== MARGIN_INFO_KEYS.KEY_IMPACT_ON_FUNDS &&
                item !== MARGIN_INFO_KEYS.KEY_IMPACT_ON_MARGIN &&
                item !== MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS &&
                item !== MARGIN_INFO_KEYS.KEY_AFTER_TRADE_MARGIN);
        }
        const account = this.get('account');
        if (!isNullOrUndefined(account)) {
            result = result.concat(Object.keys(account.GetAssetBalanceCorrect().AssetAdditionalInfo));
        }
        return result;
    }
    GetEstAvailableCashFormatted() {
        const acc = this.get('account');
        const msg = this.lastReceivedResponseMessage;
        if (isNullOrUndefined(acc) || isNullOrUndefined(msg) || isNullOrUndefined(msg.EstAvailableCash)) {
            return '';
        }
        return acc.formatPrice(msg.EstAvailableCash);
    }
    GetFreeCommissionValue() {
        const msg = this.lastReceivedResponseMessage;
        if (isNullOrUndefined(msg) || !isValidNumber(msg.FreeCommission)) {
            return 0;
        }
        return msg.FreeCommission;
    }
    GetFreeCommissionFormatted() {
        const acc = this.get('account');
        const msg = this.lastReceivedResponseMessage;
        if (isNullOrUndefined(acc) || isNullOrUndefined(msg) || isNullOrUndefined(msg.FreeCommission)) {
            return '';
        }
        return acc.formatPrice(msg.FreeCommission, true);
    }
    GetSpreadInitLossFormatted() {
        const ins = this.get('instrument');
        const acc = this.get('account');
        const qty = this.get('amount');
        const sp = DataCache.GetSpreadPlan(acc);
        if (isNullOrUndefined(acc) ||
            isNullOrUndefined(ins) ||
            isNullOrUndefined(qty) ||
            isNullOrUndefined(ins.LastQuote)) {
            return StringUtils.THREE_DASHES;
        }
        const crossPrice = DataCache.CrossRateCache.GetCrossPriceInsSideExp2(acc, ins, this.getSide()); // постоянно меняющаяся формула из доки https://docs.google.com/document/d/1-Dq-3OjRZUbWq-ppOJI0cpmqOxCP-qUCQYv20ZjTYF0
        const ask = ins.LastQuote.AskSpread_SP_Ins(sp, ins);
        const bid = ins.LastQuote.BidSpread_SP_Ins(sp, ins);
        const amount = Quantity.toLots(qty, ins);
        let result = -(ask - bid) * amount * crossPrice;
        result *= ins.getLotSize();
        result *= ins.GetTickCost();
        return result !== 0 ? acc.formatPrice(result) : StringUtils.THREE_DASHES;
    }
    GetPLPerTickFormatted() {
        var _a;
        const acc = this.get('account');
        const ins = this.get('instrument');
        const qty = this.get('amount');
        if (isNullOrUndefined(acc) || isNullOrUndefined(ins) || isNullOrUndefined(qty)) {
            return null;
        }
        let result = null;
        const crossPrice = DataCache.CrossRateCache.GetCrossPriceInsSideExp2(acc, ins, this.getSide());
        const amount = Quantity.toLots(qty, ins);
        if (MarginOEControl.TICKCOST_INS_TYPES.includes(ins.InstrType) &&
            ins.QuotingType === QuotingType.TickCost_TickSize) {
            result = ins.FuturesTickCoast * amount * crossPrice;
        }
        else {
            let price = (_a = this.GetLimitPrice()) !== null && _a !== void 0 ? _a : this.GetStopPrice();
            if (!isValidNumber(price) || price === 0) {
                const quote = ins.GetLastQuote(QuoteValid.Valid);
                if (!isNullOrUndefined(quote)) {
                    price = this.IsBuy() ? quote.Ask : quote.Bid;
                }
            }
            const tickSize = ins.getLotSize() * ins.getPointSize(price);
            result = tickSize * amount * crossPrice;
        }
        result *= ins.GetFaceValueMultiplier(); /* #127527 */
        return acc.formatPrice(result);
    }
    GetGroupBySide(side) {
        const msg = this.lastReceivedResponseMessage;
        return side === OperationType.Buy ? msg === null || msg === void 0 ? void 0 : msg.BuyGroup : msg === null || msg === void 0 ? void 0 : msg.SellGroup;
    }
    GetImpactOnMarginFormatted(assetBalanceItem, afterTradeMarginValue) {
        const availableMargin = assetBalanceItem.getColumnValue(MarginOEControl.KEYS_MAP[MARGIN_INFO_KEYS.KEY_MARGIN_AVAILABLE]);
        if (afterTradeMarginValue == null || availableMargin == null) {
            return '';
        }
        return this.formatPrice(afterTradeMarginValue - availableMargin);
    }
    GetAvailableFundsDependsOnBuyingPowerName(assetBalanceItem) {
        // #106952+
        if (isNullOrUndefined(assetBalanceItem)) {
            return null;
        }
        const assetAdditionalInfo = !isNullOrUndefined(assetBalanceItem.AssetBalance)
            ? assetBalanceItem.AssetBalance.AssetAdditionalInfo
            : null;
        const buyingPowerName = this.GetBuyingPowerName();
        if (!isNullOrUndefined(assetAdditionalInfo === null || assetAdditionalInfo === void 0 ? void 0 : assetAdditionalInfo[buyingPowerName])) {
            return assetAdditionalInfo[buyingPowerName].Value;
        }
        return assetBalanceItem.getColumnData(MarginOEControl.KEYS_MAP[MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS]).Value;
    }
    formatPrice(price, withoutAsset = false) {
        if (withoutAsset) {
            return PriceFormatter.formatPrice(price, 2);
        }
        const acc = this.get('account');
        if (!isNullOrUndefined(acc)) {
            return acc.formatPrice(price);
        }
        return price.toString();
    }
    GetCommissionGroups(commissionType, operationMode) {
        const msg = this.lastReceivedResponseMessage;
        if (isNullOrUndefined(msg === null || msg === void 0 ? void 0 : msg.CommissionsByOperationMode)) {
            return null;
        }
        const commissionGroups = msg.CommissionsByOperationMode[operationMode];
        const resultArr = [];
        if (isValidArray(commissionGroups)) {
            for (let i = 0; i < commissionGroups.length; i++) {
                const commissionGroup = commissionGroups[i];
                if (commissionGroup.CommissionType === commissionType) {
                    resultArr.push(commissionGroup);
                }
            }
        }
        return resultArr;
    }
    getSide() {
        return this.get('operation');
    }
    IsBuy() {
        return this.getSide() === OperationType.Buy;
    }
    getCommissionOperationType() {
        return this.IsBuy() ? CommissionOperationType.BUY : CommissionOperationType.SHORT;
    }
    GetFeeAmountFormatted(commissionType) {
        const value = this.GetFeeAmount(commissionType, this.getCommissionOperationType());
        return isValidNumber(value)
            ? this.formatPrice(value, true)
            : '';
    }
    GetFeeAmount(commissionType, operationMode) {
        const commissionGroups = this.GetCommissionGroups(commissionType, operationMode);
        if (!isValidArray(commissionGroups))
            return null;
        return commissionGroups.reduce((sum, group) => sum + group.Amount, 0);
    }
    GetFeeCurrencyID(commissionType, operationMode) {
        const commissionGroups = this.GetCommissionGroups(commissionType, operationMode);
        if (isValidArray(commissionGroups)) {
            return commissionGroups[0].CurrencyId;
        } // судя по настройкам сервера комиссия в разных группах не модет отличаться -> берем из первой группы
        return null;
    }
    GetFillPerLotFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.PerLot);
    }
    GetOrderPerLotFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.OrderPerLot);
    }
    GetOrderPerLotWithFixedPartFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.OrderPerLotWithFixedPart);
    }
    GetPerFillFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.PerFill);
    }
    GetPerTransactionFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.PerTransaction);
    }
    GetPerPhoneTransactionFeeFormatted() {
        return ''; // TODO выяснить, вероятно устарело, десктоп не показывает в margin OE
    }
    GetVatFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.VAT);
    }
    GetFillVolumeFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.PerVolume);
    }
    GetOrderVolumeFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.PerOrderVolume);
    }
    GetFillVolumeWithMinPDFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.VolumeWithMinPD);
    }
    GetCareOrderFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.ShortCommissionInfo);
    }
    GetFillFaceValueFeeFormatted() {
        return this.GetFeeAmountFormatted(CommissionTypes.FillFaceValue);
    }
    GetShortSwapInfo() {
        const msg = this.lastReceivedResponseMessage;
        const propName = 'ShortSwapInfo';
        let res = null;
        if (msg && Object.prototype.hasOwnProperty.call(msg, propName)) {
            res = msg[propName];
        }
        if (!isValidString(res)) {
            res = Resources.getResource('InstrumentDetailsPanel.None');
        }
        return res;
    }
    GetSwapValue(group) {
        var _a;
        return (_a = group === null || group === void 0 ? void 0 : group.Swap) !== null && _a !== void 0 ? _a : null;
    }
    GetSwapFormatted(side) {
        const msg = this.lastReceivedResponseMessage;
        if (isNullOrUndefined(msg)) {
            return StringUtils.THREE_DASHES;
        }
        const group = side === OperationType.Buy ? msg.BuyGroup : msg.SellGroup;
        const swapValue = this.GetSwapValue(group);
        return isValidNumber(swapValue) ? this.formatPrice(swapValue, true) : StringUtils.THREE_DASHES;
    }
    CalculateTotalFee(operationMode) {
        let totalFee = 0;
        Object.values(CommissionTypes).forEach((feeType) => {
            const value = this.GetFeeAmount(feeType, operationMode);
            if (isValidNumber(value))
                totalFee += value;
        });
        if (!isValidNumber(totalFee))
            return 0;
        const freeCommission = this.GetFreeCommissionValue();
        return freeCommission !== 0 && totalFee >= 0 ? Math.max(0, totalFee - freeCommission) : totalFee;
    }
    GetTotalFee(group) {
        var _a;
        if (group == null) {
            group = this.GetGroupBySide(this.getSide());
        }
        return (_a = group === null || group === void 0 ? void 0 : group.TotalFee) !== null && _a !== void 0 ? _a : this.CalculateTotalFee(this.getCommissionOperationType());
    }
    GetTotalFeeFormatted(group) {
        return this.formatPrice(this.GetTotalFee(group), true);
    }
    IsTotalFeeRowVisible() {
        if (this.GetFreeCommissionValue() !== 0)
            return true;
        const operationMode = CommissionOperationType.BUY;
        let count = 0;
        for (const key of Object.keys(CommissionTypes)) {
            if (isValidNumber(this.GetFeeAmount(CommissionTypes[key], operationMode)) && ++count > 1) {
                return true;
            }
        }
        return false;
    }
    static GetSortIndex(key) {
        switch (key) {
            case MARGIN_INFO_KEYS.KEY_BALANCE:
                return 0;
            case MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS:
                return 1;
        }
        if (!MarginOEControl.ALL_FIELDS.includes(key)) {
            return 2;
        }
        return 100;
    }
    static isColoredField(key, assetBalanceItem) {
        // тут перечислены подкрашиваемые поля
        switch (key) {
            case MARGIN_INFO_KEYS.KEY_BALANCE:
            case MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS:
            case MARGIN_INFO_KEYS.KEY_INCOMING_FUNDS:
            case MARGIN_INFO_KEYS.KEY_MARGIN_AVAILABLE:
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS:
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_MARGIN:
            case MARGIN_INFO_KEYS.KEY_IMPACT_ON_MARGIN:
            case MARGIN_INFO_KEYS.KEY_IMPACT_ON_FUNDS:
            case MARGIN_INFO_KEYS.KEY_BLOCKED_FOR_STOCKS:
            case MARGIN_INFO_KEYS.KEY_SPREAD_INIT_LOSS:
            case MARGIN_INFO_KEYS.KEY_PL_PER_TICK:
            case MARGIN_INFO_KEYS.KEY_EST_AVAILABLE_CASH:
            case MARGIN_INFO_KEYS.BLOCKED_FOR_FIXED_INCOME:
                return true;
        }
        const addInfo = assetBalanceItem.AssetBalance.AssetAdditionalInfo;
        if (!isNullOrUndefined(addInfo === null || addInfo === void 0 ? void 0 : addInfo[key])) {
            return true; // custom available funds #106952
        }
        return false;
    }
    static KeyToCommissionType(key) {
        switch (key) {
            case MARGIN_INFO_KEYS.KEY_FILL_PER_LOT:
                return CommissionTypes.PerLot;
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT:
                return CommissionTypes.OrderPerLot;
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
                return CommissionTypes.OrderPerLotWithFixedPart;
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME:
                return CommissionTypes.PerVolume;
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME_WITH_MIN_PD:
                return CommissionTypes.VolumeWithMinPD;
            case MARGIN_INFO_KEYS.KEY_PER_FILL:
                return CommissionTypes.PerFill;
            case MARGIN_INFO_KEYS.KEY_PER_TRANSACTION:
                return CommissionTypes.PerTransaction;
            case MARGIN_INFO_KEYS.KEY_PER_PHONE_TRANSACTION:
                return CommissionTypes.PerPhoneTransaction;
            case MARGIN_INFO_KEYS.KEY_VAT:
                return CommissionTypes.VAT;
            case MARGIN_INFO_KEYS.KEY_ORDER_VOLUME:
                return CommissionTypes.PerOrderVolume;
            case MARGIN_INFO_KEYS.KEY_LONG_SWAP:
                return CommissionTypes.SwapBuy;
            case MARGIN_INFO_KEYS.KEY_SHORT_SWAP:
                return CommissionTypes.SwapSell;
            case MARGIN_INFO_KEYS.KEY_ORDER_CARE:
                return CommissionTypes.ShortCommissionInfo;
            case MARGIN_INFO_KEYS.KEY_FILL_FACE_VALUE:
                return CommissionTypes.FillFaceValue;
        }
        return null;
    }
    static GetGroupForKey(headerKey) {
        switch (headerKey) {
            case MARGIN_INFO_KEYS.KEY_BALANCE:
            case MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS:
            case MARGIN_INFO_KEYS.KEY_INCOMING_FUNDS:
            case MARGIN_INFO_KEYS.KEY_MARGIN_AVAILABLE:
            case MARGIN_INFO_KEYS.KEY_IMPACT_ON_FUNDS:
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS:
            case MARGIN_INFO_KEYS.KEY_BLOCKED_FOR_STOCKS:
            case MARGIN_INFO_KEYS.KEY_SPREAD_INIT_LOSS:
            case MARGIN_INFO_KEYS.KEY_PL_PER_TICK:
            case MARGIN_INFO_KEYS.KEY_ALLOW_SHORT_POSITIONS:
            case MARGIN_INFO_KEYS.KEY_EST_AVAILABLE_CASH:
                return MARGIN_INFO_KEYS.KEY_GROUP_RISKS;
            case MARGIN_INFO_KEYS.KEY_FILL_PER_LOT:
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT:
            case MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT_WITH_FIXED_PART:
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME:
            case MARGIN_INFO_KEYS.KEY_FILL_VOLUME_WITH_MIN_PD:
            case MARGIN_INFO_KEYS.KEY_PER_FILL:
            case MARGIN_INFO_KEYS.KEY_PER_TRANSACTION:
            case MARGIN_INFO_KEYS.KEY_PER_PHONE_TRANSACTION:
            case MARGIN_INFO_KEYS.KEY_VAT:
            case MARGIN_INFO_KEYS.KEY_ORDER_VOLUME:
            case MARGIN_INFO_KEYS.KEY_SHORT_SWAP_INFO:
            case MARGIN_INFO_KEYS.KEY_TOTAL_FEES:
            case MARGIN_INFO_KEYS.KEY_FREE_COMMISSION:
            case MARGIN_INFO_KEYS.KEY_LONG_SWAP:
            case MARGIN_INFO_KEYS.KEY_SHORT_SWAP:
            case MARGIN_INFO_KEYS.KEY_ORDER_CARE:
            case MARGIN_INFO_KEYS.KEY_FILL_FACE_VALUE:
                return MARGIN_INFO_KEYS.KEY_GROUP_FEES;
            default: // for custom available funds #106952
                return MARGIN_INFO_KEYS.KEY_GROUP_RISKS;
        }
    }
    SetColumnsColouringMode(table) {
        this.ProcessSetColumnsColouringMode(table, [1], ColouringModes.Signed);
    }
    GetPriceByName(paramName) {
        return OrderEditBaseUtils.GetPriceByName(this, paramName);
    }
    GetLimitPrice() {
        return OrderEditBaseUtils.GetLimitPrice(this);
    }
    GetStopPrice() {
        return OrderEditBaseUtils.GetStopPrice(this);
    }
    GetTrailingStopPrice() {
        return OrderEditBaseUtils.GetTrailingStopPrice(this);
    }
    GetDynamicItems() {
        return [];
    }
    getClientPanel() {
        return this.find('div');
    }
    repopulate() {
        super.repopulate();
        this.InitializeDataProvider();
    }
    GetDynPropertyControlTypeForKey(key) {
        if (!isValidString(this.GetBuyingPowerName())) {
            return DynProperty.STRING;
        }
        switch (key) {
            case MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS:
                return DynProperty.INFO_PICTURE_RIGHT_AND_TEXT;
            default:
                return DynProperty.STRING;
        }
    }
    SetEditingInfo(row, controlType) {
        super.SetEditingInfo(row, controlType);
        if (controlType !== DynProperty.STRING) {
            const cells = row.cells;
            const cellLabel = cells[0];
            const cellValue = cells[1];
            cellLabel.ReadOnly = false;
            cellLabel.QuickTableEditingInfo = new QuickTableEditingInfo(controlType);
            cellLabel.QuickTableEditingInfo.GetDataHandler = this.GetBuyingPowerName.bind(this);
            cellValue.ReadOnly = true;
            cellValue.QuickTableEditingInfo = new QuickTableEditingInfo(DynProperty.STRING);
        }
    }
    GetBuyingPowerName() {
        // #106952
        const msg = this.lastReceivedResponseMessage;
        if (!isValidString(msg === null || msg === void 0 ? void 0 : msg.BuyingPowerName)) {
            return null;
        }
        return msg.BuyingPowerName;
    }
    onPaintedPictureButtonClick(data, event) {
        // if (data.controlType === DynProperty.INFO_PICTURE_RIGHT_AND_TEXT)
        // {
        //     let text = this.GetBuyingPowerName()
        //     RactiveTooltip.showTooltip();
        //     return
        // }
    }
    DisposeSource() {
        /// на смену Source отписка и пр.
    }
    // Refresh Button Region
    InitRefreshBtnTimer() {
        if (!isNullOrUndefined(this.refreshIntervalHandler)) {
            clearInterval(this.refreshIntervalHandler);
            this.refreshIntervalHandler = null;
        }
        this.refreshIntervalHandler = setInterval(this.RefreshBtnIntervalTick.bind(this), this.refreshTickTime);
    }
    RefreshBtnIntervalTick() {
        if (this.refreshTick * this.refreshTickTime >= this.refreshCycleFullTime) {
            this.RefreshByMarginRequest();
        }
        const tick = this.refreshTick++ * this.refreshTickTime;
        const deg = (tick / this.refreshCycleFullTime) * 360; // % 360
        this.drawTimerSector(deg);
    }
    drawTimerSector(deg) {
        const radius = 7.5;
        const totalLength = 2 * Math.PI * radius;
        const percent = deg / 360;
        const offset = totalLength * (1 - percent);
        void this.set('progressOffset', offset);
    }
}
MarginOEControl.TICKCOST_INS_TYPES = [
    InstrumentTypes.FUTURES,
    InstrumentTypes.CFD_FUTURES,
    InstrumentTypes.OPTIONS,
    InstrumentTypes.SPREADBET
];
// fields and their column_id from AssetBalanceItem where it possible
MarginOEControl.KEYS_MAP = (function () {
    const keys = {}; // тут также задается в каком порядке идут строки
    // 1.Risks
    keys[MARGIN_INFO_KEYS.KEY_BALANCE] = AssetBalanceItemColumnsEnum.BALANCE_COL_INDEX;
    keys[MARGIN_INFO_KEYS.KEY_AVAILABLE_FUNDS] = AssetBalanceItemColumnsEnum.AVAILABLE_FUNDS_COL_INDEX;
    keys[MARGIN_INFO_KEYS.KEY_INCOMING_FUNDS] = AssetBalanceItemColumnsEnum.INCOMING_FUNDS_COL_INDEX;
    keys[MARGIN_INFO_KEYS.KEY_MARGIN_AVAILABLE] = AssetBalanceItemColumnsEnum.MARGIN_AVAILABLE_COL_INDEX;
    keys[MARGIN_INFO_KEYS.KEY_IMPACT_ON_FUNDS] = null;
    keys[MARGIN_INFO_KEYS.KEY_IMPACT_ON_MARGIN] = null;
    keys[MARGIN_INFO_KEYS.KEY_AFTER_TRADE_FUNDS] = null;
    keys[MARGIN_INFO_KEYS.KEY_AFTER_TRADE_MARGIN] = null;
    keys[MARGIN_INFO_KEYS.KEY_BLOCKED_FOR_STOCKS] = AssetBalanceItemColumnsEnum.BLOCKED_FOR_STOCKS_COL_INDEX;
    keys[MARGIN_INFO_KEYS.KEY_SPREAD_INIT_LOSS] = null;
    keys[MARGIN_INFO_KEYS.KEY_PL_PER_TICK] = null;
    keys[MARGIN_INFO_KEYS.KEY_ALLOW_SHORT_POSITIONS] = null;
    keys[MARGIN_INFO_KEYS.KEY_EST_AVAILABLE_CASH] = null;
    keys[MARGIN_INFO_KEYS.BLOCKED_FOR_FIXED_INCOME] = AssetBalanceItemColumnsEnum.BLOCKED_FOR_FIXED_INCOME;
    // 2.Fees
    keys[MARGIN_INFO_KEYS.KEY_FILL_PER_LOT] = null;
    keys[MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT] = null;
    keys[MARGIN_INFO_KEYS.KEY_ORDER_PER_LOT_WITH_FIXED_PART] = null;
    keys[MARGIN_INFO_KEYS.KEY_FILL_VOLUME] = null;
    keys[MARGIN_INFO_KEYS.KEY_ORDER_VOLUME] = null;
    keys[MARGIN_INFO_KEYS.KEY_PER_FILL] = null;
    keys[MARGIN_INFO_KEYS.KEY_PER_TRANSACTION] = null;
    keys[MARGIN_INFO_KEYS.KEY_PER_PHONE_TRANSACTION] = null;
    keys[MARGIN_INFO_KEYS.KEY_FILL_VOLUME_WITH_MIN_PD] = null;
    keys[MARGIN_INFO_KEYS.KEY_VAT] = null;
    keys[MARGIN_INFO_KEYS.KEY_SHORT_SWAP_INFO] = null;
    keys[MARGIN_INFO_KEYS.KEY_FREE_COMMISSION] = null;
    keys[MARGIN_INFO_KEYS.KEY_TOTAL_FEES] = null;
    keys[MARGIN_INFO_KEYS.KEY_LONG_SWAP] = null;
    keys[MARGIN_INFO_KEYS.KEY_SHORT_SWAP] = null;
    keys[MARGIN_INFO_KEYS.KEY_ORDER_CARE] = null;
    keys[MARGIN_INFO_KEYS.KEY_FILL_FACE_VALUE] = null;
    return keys;
})();
// array of all available keys = fields
MarginOEControl.ALL_FIELDS = (function () {
    return Object.keys(MarginOEControl.KEYS_MAP);
})();
VerticalApplicationPanelNew.extendWith(MarginOEControl, {
    data: function () {
        return {
            account: null,
            instrument: null,
            operation: null,
            orderType: null,
            tif: null,
            price: null,
            stopPrice: null,
            amount: null,
            cash: null,
            isCashQtyMode: false, // #122525
            estQtyValue: null,
            productType: null,
            leverageValue: null,
            dockablePanel: false,
            attached: false,
            orderEditParameterArray: null,
            refreshBtnTooltip: '',
            progressOffset: 47.1
        };
    },
    template: MarginOEControlTemplate
});
